import { Component, inject, OnInit } from '@angular/core';
import { MenuComponentBase } from '../../../../shared/base-classes/menu-component-base';
import {
  CustomerPortalFlow,
  ConnectionUser,
  SubscriptionDetails,
  SubscriptionPlanMenu,
  SubscriptionPlanPrice,
  SwitchPlan,
  TransactionHistory,
} from '../../models/subscription';
import { CommonModule } from '@angular/common';
import { SubscriptionService } from '../../services/subscription.service';
import { takeUntil } from 'rxjs';
import { Router } from '@angular/router';
import { SkeletonComponent } from '../../../../shared/components/skeleton/skeleton.component';
import { PopupComponent } from '../../../../shared/components/popup/popup.component';
import { ToastType } from '../../../../shared/interfaces/common';
import { CommonService } from '../../../../shared/services/common/common.service';
import { AuthService } from '../../../auth/services/auth.service';
import { PermissionCode } from '../../../../shared/enums/permission-code';
import { SvgIconComponent } from '../../../../shared/components/svg-icon/svg-icon.component';

@Component({
  selector: 'app-manage-subscriptions',
  templateUrl: './manage-subscriptions.component.html',
  styleUrls: ['./manage-subscriptions.component.scss'],
  standalone: true,
  imports: [CommonModule, SkeletonComponent, PopupComponent, SvgIconComponent],
})
export class ManageSubscriptionsComponent
  extends MenuComponentBase
  implements OnInit
{
  private subscriptionService = inject(SubscriptionService);
  private router = inject(Router);
  private commonService = inject(CommonService);
  private authService = inject(AuthService);

  isSubscriptionAdmin = this.authService.hasPermission(
    PermissionCode.MANAGE_SUBSCRIPTION,
  );

  showSubscriptionPlansPopup = false;

  subscription!: SubscriptionDetails;

  subscriptionPlans: SubscriptionPlanMenu[] = [];

  transactionHistory: TransactionHistory[] = [];

  billingInterval: 'month' | 'year' = 'month';

  isDowngrade = false;
  isDowngradePopupOpen = false;
  targetPlan: SubscriptionPlanMenu | null = null;

  getPlanPrice(
    plan: SubscriptionPlanMenu,
    interval: 'month' | 'year',
  ): SubscriptionPlanPrice | undefined {
    return plan.prices.find((price) => price.billing_interval === interval);
  }

  subscriptionAdmins: ConnectionUser[] = [];
  isLoadingAdmins = false;

  get hasActiveWarning(): boolean {
    if (!this.subscription) return false;
    return Boolean(
      (this.subscription.cancel_at_period_end &&
        this.subscription.status === 'active') ||
        this.subscription.webhook_status === 'processing' ||
        this.subscription.pending_plan,
    );
  }

  hasAdminName(admin: ConnectionUser): boolean {
    return !!(admin.first_name?.trim() || admin.last_name?.trim());
  }

  getAdminFullName(admin: ConnectionUser): string {
    const fullName =
      `${admin.first_name || ''} ${admin.last_name || ''}`.trim();
    return fullName || admin.email || 'Admin';
  }

  getAdminInitials(admin: ConnectionUser): string {
    const first = admin.first_name?.trim()?.charAt(0) || '';
    const last = admin.last_name?.trim()?.charAt(0) || '';
    const initials = `${first}${last}`.toUpperCase();
    return initials || admin.email?.charAt(0)?.toUpperCase() || 'A';
  }

  fetchSubscriptionAdmins(): void {
    if (this.isSubscriptionAdmin) return;
    this.isLoadingAdmins = true;
    this.subscriptionService
      .getSubscriptionAdmins()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (admins) => {
          this.subscriptionAdmins = admins || [];
          this.isLoadingAdmins = false;
        },
        error: (err) => {
          this.isLoadingAdmins = false;
          console.error('Failed to fetch subscription admins', err);
        },
      });
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.fetchSubscriptionDetails();
    if (!this.isSubscriptionAdmin) {
      this.fetchSubscriptionAdmins();
    }
  }

  getVisiblePlans() {
    const plans =
      this.subscription.plan_name !== 'free_plan' &&
      (this.subscription.status === 'active' ||
        this.subscription.status === 'trialing')
        ? this.subscriptionPlans.filter((plan) =>
            plan.prices.some(
              (price) =>
                price.billing_interval === this.billingInterval &&
                plan.plan_name !== 'free_plan',
            ),
          )
        : this.subscriptionPlans.filter((plan) =>
            plan.prices.some(
              (price) => price.billing_interval === this.billingInterval,
            ),
          );

    return [...plans].sort((a, b) => {
      const aPrice = this.getPlanPrice(a, this.billingInterval)?.amount ?? 0;
      const bPrice = this.getPlanPrice(b, this.billingInterval)?.amount ?? 0;
      return Number(aPrice) - Number(bPrice);
    });
  }

  isLoading = false;

  fetchSubscriptionDetails() {
    this.isLoading = true;
    this.subscriptionService
      .getSubscriptionDetails()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.subscription = res;
          if (this.subscription.status === 'canceled') {
            this.subscription = {
              ...res,
              plan_name: 'free_plan',
              status: 'active',
              billing_interval: undefined,
              pending_plan: undefined,
              current_period_start: undefined,
              current_period_end: undefined,
              cancel_at_period_end: false,
            };
          } else {
            this.subscription = res;
          }

          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          this.commonService.triggerToast({
            type: ToastType.Error,
            message:
              error?.detail ||
              error?.[0] ||
              'Failed to fetch subscription details.',
          });
        },
      });
  }

  fetchSubscriptionPlans() {
    this.subscriptionService
      .getSubscriptionPlans()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.showSubscriptionPlansPopup = true;
          this.subscriptionPlans = res;
        },
        error: (error) => {
          this.commonService.triggerToast({
            type: ToastType.Error,
            message:
              error?.detail ||
              error?.[0] ||
              'Failed to fetch subscription plans.',
          });
        },
      });
  }

  openPlans(): void {
    if (!this.isSubscriptionAdmin) return;
    if (this.subscriptionPlans.length === 0) {
      this.fetchSubscriptionPlans();
    } else {
      this.showSubscriptionPlansPopup = true;
    }
  }

  switchPlan(plan: SubscriptionPlanMenu): void {
    if (
      this.subscription.plan_name === 'free_plan' ||
      this.subscription.status !== 'active'
    ) {
      this.createCheckoutSession(plan);
      return;
    }
    this.targetPlan = plan;
    const currentPlanPrice = this.subscription.amount;
    const taretPrice = plan.prices.find(
      (price) => price.billing_interval === this.billingInterval,
    )?.amount;

    if (currentPlanPrice && taretPrice) {
      this.isDowngrade = currentPlanPrice > taretPrice;
    }

    this.isDowngradePopupOpen = true;
  }

  closePlans(): void {
    this.isDowngradePopupOpen = false;
    this.targetPlan = null;
    this.showSubscriptionPlansPopup = false;
  }

  cancelSwitchPlan(): void {
    this.isDowngradePopupOpen = false;
    this.targetPlan = null;
  }

  cancelSubscription(): void {
    if (!this.isSubscriptionAdmin) return;
    this.redirectToCustomerPortal(CustomerPortalFlow.CancelSubscription);
  }

  viewTransactions(): void {
    if (!this.isSubscriptionAdmin) return;
    this.router.navigate(['/transactions']);
  }

  redirectToCustomerPortal(flow: CustomerPortalFlow): void {
    this.subscriptionService
      .redirectToCustomerPortal(flow)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          window.location.href = response.url;
        },
        error: (error) => {
          this.commonService.triggerToast({
            type: ToastType.Error,
            message:
              error?.detail ||
              error?.[0] ||
              'Failed to redirect to customer portal.',
          });
        },
      });
  }

  manageSubscription(): void {
    if (!this.isSubscriptionAdmin) return;
    this.redirectToCustomerPortal(CustomerPortalFlow.Billing);
  }

  createCheckoutSession(planDetails: SubscriptionPlanMenu): void {
    this.subscriptionService
      .createCheckoutSession(planDetails.plan_name, this.billingInterval)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          window.location.href = response.checkout_url;
        },
        error: (error) => {
          this.commonService.triggerToast({
            type: ToastType.Error,
            message:
              error?.detail ||
              error?.[0] ||
              'Failed to create checkout session.',
          });
        },
      });
  }

  manageSwitchPlan(): void {
    const payload: SwitchPlan = {
      target_plan: this.targetPlan!.plan_name,
      billing_interval: this.billingInterval,
    };
    this.subscriptionService
      .switchPlan(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.subscription = response;
          this.isDowngradePopupOpen = false;
          this.targetPlan = null;
          this.showSubscriptionPlansPopup = false;
          this.commonService.triggerToast({
            type: ToastType.Success,
            message: 'Subscription plan switched successfully.',
          });
        },
        error: (error) => {
          this.commonService.triggerToast({
            type: ToastType.Error,
            message:
              error?.detail ||
              error?.[0] ||
              'Failed to switch subscription plan.',
          });
          this.isDowngradePopupOpen = false;
        },
      });
  }
}
